import React, { useState, useEffect } from 'react'
import { useApolloClient, useQuery } from '@apollo/client'
import { useCart } from '../contexts/CartContext'
import { useTerminal } from '../contexts/TerminalContext'
import { OrderService } from '../lib/orderService'
import { PaymentService } from '../lib/paymentService'
import { ReceiptService } from '../lib/receiptService'
import { ReceiptUtils } from '../lib/receiptUtils'
import { CartTransformationService } from '../lib/cartTransformation'
import { useOrderCharges } from '../hooks/useOrderCharges'
import { auth } from '../lib/auth'
import { usePermissions } from '../hooks/usePermissions'
import { PERMISSIONS } from '../lib/permissions'
import toast from 'react-hot-toast'
import { Order, PaymentMethod, Payment, Receipt } from '../types/order'
import { XMarkIcon, CreditCardIcon, BanknotesIcon, ShareIcon, EyeIcon, PrinterIcon } from '@heroicons/react/24/outline'
import { GET_ACTIVE_USERS, GET_ORDER } from '../graphql/queries'
import { GET_PRODUCT_AVAILABILITY } from '../graphql/product-queries'

interface CheckoutModalProps {
  isOpen: boolean
  couponCode?: string
  /** Pre-computed discounted total from the discount preview. When set, this is sent
   *  as total_cents so the API's price-integrity check passes. */
  discountedTotalCents?: number
  /** Curry Me Club counter code (§4/§5). Sent as loyalty.counter_code; the
   *  discountedTotalCents already nets the reward. The code is consumed once the
   *  order row exists — a second create answers "Counter code is invalid or expired". */
  counterCode?: string
  onClose: () => void
  onOrderCreated: (order: Order) => void
  onError: (error: string) => void
}

const CheckoutModal: React.FC<CheckoutModalProps> = ({
  isOpen,
  couponCode,
  discountedTotalCents,
  counterCode,
  onClose,
  onOrderCreated,
  onError
}) => {
  const { cart, clearCart } = useCart()
  const { selectedTerminal } = useTerminal()
  // Statiegeld: outside BTW, never discounted, always charged. cart.total_cents
  // and discountedTotalCents are both GOODS-only, so every amount the customer
  // actually pays is goods + deposit — use payableTotalCents for all of them.
  const depositTotalCents = CartTransformationService.calculateCartDepositCents(cart)
  // Verpakkingskosten: 0 for dine-in, resolved by the same rule the API uses.
  // Same question, same Apollo cache entry as the cart — one answer, so the
  // screen the cashier reads out and the total submitted cannot disagree.
  const { packagingFeeCents } = useOrderCharges(cart.order_type)
  const payableTotalCents =
    (discountedTotalCents ?? cart.total_cents) + depositTotalCents + packagingFeeCents
  const apolloClient = useApolloClient()
  const { hasPermission } = usePermissions()
  const [isProcessing, setIsProcessing] = useState(false)
  const [customerName, setCustomerName] = useState('')
  const [notes, setNotes] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('CARD')
  const [consumingEmployeeId, setConsumingEmployeeId] = useState<number | null>(null)
  const [paymentState, setPaymentState] = useState<'idle' | 'processing' | 'completed' | 'failed' | 'pending' | 'cancelled'>('idle')
  const [currentOrder, setCurrentOrder] = useState<Order | null>(null)
  const [currentReceipt, setCurrentReceipt] = useState<Receipt | null>(null)
  const [merchantReceipt, setMerchantReceipt] = useState<Receipt | null>(null)
  const [isLoadingCustomerReceipt, setIsLoadingCustomerReceipt] = useState(false)
  const [isLoadingMerchantReceipt, setIsLoadingMerchantReceipt] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)
  const [hasRequestedCancel, setHasRequestedCancel] = useState(false)
  const [paymentError, setPaymentError] = useState<string | null>(null)
  const [showCashConfirmation, setShowCashConfirmation] = useState(false)
  const pollingIntervalRef = React.useRef<number | null>(null)
  const [pollingAttempts, setPollingAttempts] = useState(0)
  const [currentOrderId, setCurrentOrderId] = useState<number | null>(null)
  // The order's number, kept beside the id so the idle view can say WHICH order a
  // retried payment goes to (currentOrder itself is cleared on "Try Payment Again").
  const [currentOrderNumber, setCurrentOrderNumber] = useState<string | null>(null)
  // Payment ID is set but not read directly - used for polling function closure
  const [_currentPaymentId, setCurrentPaymentId] = useState<number | null>(null)
  const [currentPayment, setCurrentPayment] = useState<Payment | null>(null)
  const [terminalMessage, setTerminalMessage] = useState<string | null>(null)
  // Action flags completion tracking
  const [completedActions, setCompletedActions] = useState<Set<string>>(new Set())

  const orderService = new OrderService(apolloClient)
  const paymentService = new PaymentService(apolloClient)
  const receiptService = new ReceiptService(apolloClient)

  const { data: activeUsersData } = useQuery(GET_ACTIVE_USERS, {
    skip: paymentMethod !== 'STAFF_MEAL',
    fetchPolicy: 'cache-and-network'
  })
  const activeUsers: Array<{ id: number; name: string }> = activeUsersData?.activeUsers || []

  // TODO: Remove SSE hook - polling will be used instead
  // Previously used usePaymentNotifications hook for SSE-based payment notifications
  // Now using polling strategy instead

  // Clear payment state when modal opens for new checkout
  useEffect(() => {
    if (isOpen) {
      // Clear state for new checkout
      setPaymentState('idle')
      setCurrentOrder(null)
      setCurrentReceipt(null)
      setMerchantReceipt(null)
      setIsLoadingCustomerReceipt(false)
      setIsLoadingMerchantReceipt(false)
      setPaymentError(null)
      setIsCancelling(false)
      setHasRequestedCancel(false)
      setPollingAttempts(0)
      setCurrentOrderId(null)
      setCurrentOrderNumber(null)
      setHasStartedPolling(false)
      setCurrentPaymentId(null)
      setCurrentPayment(null)
      setTerminalMessage(null)
      setCompletedActions(new Set())
      setPaymentMethod('CARD')
      setConsumingEmployeeId(null)
      // Clear the order note so an allergy/prep note never carries into the next customer's order
      setNotes('')
      // Clear any existing polling
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [isOpen])


  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
      }
    }
  }, [])

  // Input handlers
  const handleCustomerNameChange = (value: string) => {
    setCustomerName(value)
  }

  const handlePaymentMethodChange = (value: PaymentMethod) => {
    setPaymentMethod(value)
    if (value !== 'STAFF_MEAL') setConsumingEmployeeId(null)
  }

  const handleCashConfirmation = (confirmed: boolean) => {
    setShowCashConfirmation(false)
    if (confirmed) {
      // Proceed with cash payment
      proceedWithCheckout()
    }
  }

  const handleCheckout = async () => {
    // Clear any previous errors
    setPaymentError(null)

    if (cart.items.length === 0) {
      setPaymentError('Cart is empty')
      return
    }

    // Staff meal validation
    if (paymentMethod === 'STAFF_MEAL') {
      if (!consumingEmployeeId) {
        setPaymentError('Please select an employee for the staff meal.')
        return
      }
      proceedWithCheckout()
      return
    }

    // Check for unsupported payment methods
    if (paymentMethod === 'ONLINE') {
      setPaymentError('Online payments are not yet implemented. Please choose Cash or Card payment.')
      return
    }

    // For card payments, require a terminal to be selected
    if (paymentMethod === 'CARD') {
      if (!selectedTerminal || selectedTerminal === '') {
        setPaymentError('Please select a payment terminal before processing card payments. Click on the terminal name next to your username to select one.')
        return
      }
    }

    // For cash payments, show confirmation first
    if (paymentMethod === 'CASH') {
      setShowCashConfirmation(true)
      return
    }

    // For non-cash payments, proceed directly
    proceedWithCheckout()
  }

  const proceedWithCheckout = async () => {
    setIsProcessing(true)
    setPaymentState('processing')

    try {
      // Additional safety check: For card payments, ensure terminal is selected
      if (paymentMethod === 'CARD' && (!selectedTerminal || selectedTerminal === '')) {
        throw new Error('Payment terminal is required for card payments. Please select a terminal and try again.')
      }

      // Transform cart to order data with payment
      // Payment is now required and must be provided
      const orderData = CartTransformationService.transformCartToOrderData(
        cart,
        paymentMethod,
        customerName || undefined,
        cart.order_type,
        notes.trim() || undefined,
        Number(auth.getUser()?.id) || 1,
        selectedTerminal,
        couponCode || undefined,
        consumingEmployeeId ?? undefined,
        packagingFeeCents
      )

      // If we have a server-previewed discounted total, send that as total_cents
      // so the API's price-integrity check agrees with what the cashier sees.
      // Skip for staff meals — server always overrides total to 0.
      //
      // The preview prices GOODS only (statiegeld is never discountable), so the
      // deposit has to be added back on top of it — otherwise this override
      // silently drops the statiegeld that transformCartToOrderData included and
      // every order with a can is rejected as "Order total has changed".
      // Both extras have to be re-added here: the preview prices GOODS only, so
      // this override would otherwise drop the statiegeld AND the packaging fee
      // that transformCartToOrderData included, and every discounted order with
      // a can or a box would fail the ±2c check.
      if (discountedTotalCents !== undefined && paymentMethod !== 'STAFF_MEAL') {
        const depositCents = CartTransformationService.calculateCartDepositCents(cart)
        const totalWithExtras = discountedTotalCents + depositCents + packagingFeeCents
        orderData.total_cents = totalWithExtras
        if (orderData.payment) {
          orderData.payment.amount_cents = totalWithExtras
        }
      }

      // Curry Me Club: the counter code names the member and their reward. Sent
      // only here (never with reward_id); the code is consumed when the order
      // row exists. A staff meal or an inapplicable reward is refused by the API
      // with a "Reward not applicable: …" message shown verbatim in the catch.
      if (counterCode) {
        orderData.loyalty = { counter_code: counterCode }
      }

      // A declined or cancelled card leaves the order standing, unpaid. "Try Payment Again"
      // keeps its id, so this attempt pays THAT order again (retryOrderPayment, API
      // 2026-10-08) instead of creating a second one for staff to void. The payment input
      // is the one createOrder takes; the amount is the server's (the order's open balance),
      // and the order keeps the type, name, notes and club code it was created with. A
      // first attempt — or one after a refusal that said to start a new order — creates.
      const order = currentOrderId
        ? await orderService.retryOrderPayment(currentOrderId, orderData.payment)
        : await orderService.createOrder(orderData)
      setCurrentOrder(order)
      setCurrentOrderId(Number(order.id))
      setCurrentOrderNumber(order.order_number)

      // Handle payment status based on the response
      await handlePaymentStatus(order)

    } catch (error) {
      const message = error instanceof Error ? error.message : 'Checkout failed'
      // A retry refused because the order is no longer awaiting payment: most likely the first
      // sale landed on the terminal AFTER the POS stopped waiting (a timeout, a cancel that came
      // too late). The cart must not stay standing to be rung up a second time — read the order
      // as it is now and, when a completed payment is on it, land on the completed view with its
      // receipt exactly as a completed poll would. Any other state (voided by staff meanwhile)
      // falls through and shows the refusal.
      if (
        currentOrderId &&
        /^Payment retry refused: .* (is not awaiting payment|has nothing left to pay)$/.test(message)
      ) {
        if (await settleAlreadyPaidOrder(currentOrderId)) return
      }
      // A retry refused because the club prize the dead payment released is back in the
      // member's wallet: the contract says start a new order, so the next attempt creates one.
      if (currentOrderId && message.startsWith('Reward is no longer attached')) {
        setCurrentOrderId(null)
        setCurrentOrderNumber(null)
      }
      // Curry Me Club (§7): a counter code that is used/expired, or a reward
      // that no longer applies (incl. "Reward is no longer available", which
      // also contains "is no longer available" but must NOT take the product
      // sold-out retry path below). Show it verbatim; the cashier removes the
      // club code and the order still goes through without the reward.
      if (/^(Counter code|Reward )/.test(message)) {
        setPaymentState('failed')
        onError(message)
      } else if (message.includes('is no longer available')) {
        setPaymentState('idle')
        // The grid was stale (e.g. sold out since the last poll): pull the state now so the
        // tile greys before the cashier retries, instead of up to one poll later.
        Promise.resolve(apolloClient.refetchQueries({ include: [GET_PRODUCT_AVAILABILITY] })).catch(() => {})
        toast(
          (t) => (
            <span>
              {message}
              <button
                onClick={() => {
                  toast.dismiss(t.id)
                  proceedWithCheckout()
                }}
                className="ml-3 underline font-semibold"
              >
                Retry
              </button>
            </span>
          ),
          { duration: 10000 }
        )
      } else {
        setPaymentState('failed')
        onError(message)
      }
    } finally {
      setIsProcessing(false)
    }
  }

  // Poll payment notifications to get terminal messages and status updates
  // Polls every 2 seconds for faster updates (terminal messages change quickly)
  // Uses message_order_number to determine the latest terminal message to display
  const startPaymentPolling = (paymentId: number, orderId: number) => {
    const pollInterval = 2000 // Poll every 2 seconds for better UX (terminal messages update quickly)
    const maxAttempts = 180 // Poll for up to 180 attempts (180 * 2 seconds = 360 seconds = 6 minutes)
    let attempts = 0
    let lastMessageOrderNumber = 0 // Track the highest message_order_number we've seen

    const poll = async () => {
      try {
        // Fetch both notifications and payment status
        const [notifications, paymentStatus] = await Promise.all([
          paymentService.getPaymentNotifications(paymentId),
          paymentService.getPaymentStatus(paymentId)
        ])
        attempts++
        
        // Update current payment with latest status
        setCurrentPayment(paymentStatus)
        
        // Check payment status from API first - this is the source of truth
        // Normalize status to uppercase for comparison (handles both 'completed' and 'COMPLETED')
        const apiStatus = (paymentStatus.status || '').toUpperCase()
        
        // Normalize type to lowercase for comparison (handles both TERMINAL_MESSAGE and terminal_message)
        const normalizeType = (type: string) => type.toLowerCase()
        
        // Find the latest terminal message with highest message_order_number
        let latestTerminalMessage: string | null = null
        let highestOrderNumber = lastMessageOrderNumber
        let paymentCompleted = false
        let finalStatus: string | null = null
        
        // Process notifications to find latest terminal message and handle status changes
        for (const notification of notifications) {
          const normalizedType = normalizeType(notification.type)
          
          // Handle terminal messages - find the one with highest message_order_number
          if (normalizedType === 'terminal_message' && notification.message) {
            const orderNumber = notification.message_order_number
            
            // Only consider messages with order_number, or use null ones if no order_number messages exist yet
            if (orderNumber !== null) {
              if (orderNumber > highestOrderNumber) {
                highestOrderNumber = orderNumber
                latestTerminalMessage = notification.message
              }
            } else if (highestOrderNumber === 0 && latestTerminalMessage === null) {
              // Fallback: use message without order_number only if we haven't seen any with order_number
              latestTerminalMessage = notification.message
            }
          }
          
          // Handle status changes
          if (normalizedType === 'status_change' && notification.status) {
            const status = notification.status.toUpperCase()
            
            // Clear terminal message when status changes to final state
            if (status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELED') {
              latestTerminalMessage = null
            }
            // UNKNOWN status can transition to final states, so keep polling
          }
          
          // Handle payment completion - this is the definitive signal
          if (normalizedType === 'payment_completed') {
            paymentCompleted = true
            finalStatus = (notification.status || 'COMPLETED').toUpperCase()
          }
        }
        
        // Handle UNKNOWN status - proceed to completed step
        // Order will be CONFIRMED, payment remains UNKNOWN, but order can be processed normally
        if (apiStatus === 'UNKNOWN') {
          clearInterval(interval)
          pollingIntervalRef.current = null
          setHasStartedPolling(false)
          setTerminalMessage(null)
          // Proceed to success/completed screen with UNKNOWN payment status
          await handlePaymentSuccess(orderId)
          return // Exit - show completed screen
        }
        
        // Check final states from API status
        if (apiStatus === 'COMPLETED' || apiStatus === 'FAILED' || apiStatus === 'CANCELED') {
          // Payment has reached a final state, stop polling and handle it
          clearInterval(interval)
          pollingIntervalRef.current = null
          setHasStartedPolling(false)
          setTerminalMessage(null)
          
          if (apiStatus === 'COMPLETED') {
            await handlePaymentSuccess(orderId)
          } else if (apiStatus === 'FAILED' || apiStatus === 'CANCELED') {
            handlePaymentFailure({ 
              status: apiStatus, 
              failure_code: null 
            } as any)
          }
          
          return // Exit early - payment is in final state
        }
        
        // If payment is completed via notification, stop polling and handle completion
        // Note: We also check API status above, so this is a backup check
        if (paymentCompleted && finalStatus) {
          clearInterval(interval)
          pollingIntervalRef.current = null
          setHasStartedPolling(false)
          setTerminalMessage(null)
          
          if (finalStatus === 'COMPLETED') {
            await handlePaymentSuccess(orderId)
          } else if (finalStatus === 'FAILED' || finalStatus === 'CANCELED') {
            // Find failure_code from notifications
            const failureNotification = notifications.find(n => 
              normalizeType(n.type) === 'payment_completed'
            )
            handlePaymentFailure({ 
              status: finalStatus, 
              failure_code: failureNotification?.failure_code 
            } as any)
          }
          
          return // Exit early - payment is complete
        }
        
        // Update terminal message if we found a newer one
        if (latestTerminalMessage !== null) {
          setTerminalMessage(latestTerminalMessage)
          lastMessageOrderNumber = highestOrderNumber
        } else {
        }
        
        // Check if we've exceeded max attempts
        // For UNKNOWN status, continue polling longer (up to 500+ seconds)
        // At t=360 seconds (180 attempts * 2 seconds), status may change to UNKNOWN
        // At t>500 seconds (250 attempts * 2 seconds), final status should be available
        const extendedMaxAttempts = 250 // 250 * 2 seconds = 500 seconds for final status
        
        if (attempts >= extendedMaxAttempts) {
          clearInterval(interval)
          pollingIntervalRef.current = null
          setHasStartedPolling(false)
          // If still UNKNOWN after extended polling, proceed to completed state
          // Order can be processed normally even with UNKNOWN payment status
          if (apiStatus === 'UNKNOWN') {
            await handlePaymentSuccess(orderId)
          } else {
            setPaymentState('pending')
            setPaymentError('Payment verification timed out. Please check with staff.')
          }
        } else {
          setPollingAttempts(attempts)
        }
      } catch (error) {
        attempts++
        if (attempts >= maxAttempts) {
          clearInterval(interval)
          pollingIntervalRef.current = null
          setHasStartedPolling(false)
          setPaymentState('failed')
          setPaymentError('Failed to verify payment status. Please try again.')
        } else {
          setPollingAttempts(attempts)
        }
      }
    }

    // Start polling immediately, then continue at interval
    poll() // First poll immediately
    const interval = window.setInterval(poll, pollInterval)
    pollingIntervalRef.current = interval
  }

  // See the catch in proceedWithCheckout: the API refused to pay the order again because it is
  // not awaiting payment. Read it fresh (network-only — the cached copy still carries the dead
  // payment from createOrder) and, when a completed sale is on it, settle: the completed view
  // reads currentOrder.payment / currentPayment, so both point at that sale. Returns false when
  // the order is not actually paid (cancelled, refunded, or the read failed) so the caller shows
  // the refusal instead.
  const settleAlreadyPaidOrder = async (orderId: number): Promise<boolean> => {
    try {
      const { data } = await apolloClient.query<{ order: Order | null }>({
        query: GET_ORDER,
        variables: { id: orderId },
        fetchPolicy: 'network-only',
      })
      const order = data?.order
      const paid = order?.payments?.find(
        p => (p.status || '').toUpperCase() === 'COMPLETED' && (p.transaction_type ?? 'SALE') === 'SALE'
      )
      if (!order || !paid || order.status === 'CANCELLED' || order.status === 'REFUNDED') return false
      setCurrentOrder({ ...order, payment: paid })
      setCurrentPayment(paid)
      await handlePaymentSuccess(orderId)
      return true
    } catch {
      return false
    }
  }

  const handlePaymentSuccess = async (orderId?: number) => {
    try {
      // Use passed orderId, then currentOrder.id, then currentOrderId as fallbacks
      const finalOrderId = orderId || (currentOrder?.id ? Number(currentOrder.id) : currentOrderId)
      
      if (!finalOrderId) {
        setPaymentState('completed')
        clearCart()
        return
      }
      
      // Load customer receipt
      try {
        const receipt = await receiptService.getCustomerReceiptByOrder(finalOrderId)
        setCurrentReceipt(receipt)
      } catch (error) {
      }
      
      setPaymentState('completed')
      
      // Clear cart immediately when payment is successful
      clearCart()
      
      // Check action flags from currentPayment - these are set during polling
      // Action flags are checked and displayed in the UI when payment status is COMPLETED
    } catch (error) {
      // Still show success even if receipt fails
      setPaymentState('completed')
      clearCart()
    }
  }
  // Load merchant receipt when needed (customer signature or ID required)
  useEffect(() => {
    const payment = currentPayment || currentOrder?.payment
    if (!payment) return
    
    const merchantReceiptNeeded =
      payment.ask_customer_signature === true ||
      payment.ask_customer_identification === true
    
    if (merchantReceiptNeeded && !merchantReceipt && currentOrderId && !isLoadingMerchantReceipt && paymentState === 'completed') {
      setIsLoadingMerchantReceipt(true)
      receiptService.getMerchantReceiptByOrder(currentOrderId)
        .then(setMerchantReceipt)
        .catch(() => {
          // Don't show error - merchant receipt might not be available
        })
        .finally(() => setIsLoadingMerchantReceipt(false))
    }
  }, [currentPayment, currentOrder, merchantReceipt, currentOrderId, isLoadingMerchantReceipt, paymentState])
  // Handle action completions
  const handleActionComplete = (actionType: string) => {
    setCompletedActions(prev => new Set(prev).add(actionType))
  }

  const handleMerchantSignatureComplete = () => {
    handleActionComplete('merchant_signature')
    // In a real implementation, you would store the signature data here
  }

  const handleCustomerSignatureComplete = () => {
    handleActionComplete('customer_signature')
    // In a real implementation, you would store the customer signature data here
  }

  const handleCustomerIDComplete = () => {
    handleActionComplete('customer_identification')
    // In a real implementation, the cashier writes the ID number on the receipt by hand
  }

  const handleReceiptPrintComplete = () => {
    if (currentReceipt) {
      ReceiptUtils.printReceipt(currentReceipt)
      handleActionComplete('print_customer_receipt')
    } else {
      setPaymentError('Receipt not available for printing')
    }
  }

  const handlePaymentFailure = (payment: any) => {
    const status = payment.status?.toUpperCase() || payment.status
    if (status === 'FAILED') {
      setPaymentState('failed')
      setPaymentError('Payment failed due to a technical error. Please try again.')
    } else if (status === 'CANCELED') {
      setPaymentState('cancelled')
      setPaymentError('Payment was cancelled on the terminal.')
    } else {
      // Fallback for unknown failure states
      setPaymentState('failed')
      setPaymentError('Payment could not be completed.')
    }
  }

  const handlePaymentStatus = async (order: Order) => {
    const payment = order.payment
    
    if (!payment) {
      setPaymentState('failed')
      setPaymentError('No payment information available')
      return
    }

    // Set payment ID and start polling for card payments
    if (payment.payment_method === 'CARD' && payment.id) {
      const paymentId = Number(payment.id)
      setCurrentPaymentId(paymentId)
      setCurrentPayment(payment)
      
      // Fetch payment status immediately to get latest status
      paymentService.getPaymentStatus(paymentId).then((updatedPayment) => {
        setCurrentPayment(updatedPayment)
      }).catch(() => {
        // Continue with the payment from order if fetch fails
      })
      
      // Start polling for payment notifications
      startPaymentPolling(paymentId, Number(order.id))
      setHasStartedPolling(true)
    }

    // Handle different payment statuses (normalize to uppercase for comparison)
    const normalizedStatus = (payment.status || '').toUpperCase()
    
    if (normalizedStatus === 'COMPLETED') {
      await handlePaymentSuccess(Number(order.id))
    } else if (normalizedStatus === 'FAILED') {
      handlePaymentFailure(payment)
    } else if (normalizedStatus === 'CANCELED') {
      handlePaymentFailure(payment)
    } else if (normalizedStatus === 'PENDING') {
      // For card payments in PENDING state, polling is already started above
      if (payment.payment_method === 'CARD') {
        setPaymentState('processing')
        // Polling is started when payment ID is set above
      } else {
        // Non-card payments (cash) should complete immediately
        // If we get PENDING for cash, it's likely an error state
        setPaymentState('processing')
        // Note: Cash payments should complete immediately, so polling shouldn't be needed
      }
    } else if (normalizedStatus === 'UNKNOWN') {
      // UNKNOWN status - proceed to completed step
      // Order will be CONFIRMED, payment remains UNKNOWN, but order can be processed normally
      // Stop any polling if it was started
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current)
        pollingIntervalRef.current = null
        setHasStartedPolling(false)
      }
      // Proceed to success/completed screen with UNKNOWN payment status
      await handlePaymentSuccess(Number(order.id))
    } else {
      // Unknown/unexpected status
      setPaymentState('failed')
      setPaymentError(`Unexpected payment status: ${payment.status}. Please try again.`)
    }
  }

  // Track if we've started polling to avoid duplicate polling
  // Set but not read directly - used for state management
  const [_hasStartedPolling, setHasStartedPolling] = useState(false)

  // TODO: Remove SSE-related useEffect hooks
  // Previously handled SSE status changes and fallback polling
  // Now polling will be the primary method

  const handleCancelPayment = async () => {
    // Check if payment can be cancelled
    const payment = currentPayment || currentOrder?.payment
    if (!payment) {
      setPaymentError('No payment information available')
      return
    }

    const paymentStatus = (payment.status || '').toUpperCase()
    const canCancel = (paymentStatus === 'PENDING' || paymentStatus === 'UNKNOWN')
      && payment.payment_method === 'CARD'

    if (!canCancel) {
      setPaymentError('Payment cannot be cancelled at this time')
      return
    }

    setIsCancelling(true)
    
    try {
      // Call the cancel mutation
      // If no error: Cancel request was sent (not success/fail)
      // Status will update via notifications when CCV processes it
      await paymentService.cancelCardPaymentById(Number(payment.id))
      
      // Cancel request was sent successfully
      // Continue polling - status will update to 'canceled' when CCV processes it
      // or stay 'pending' if cancel is rejected
      // The polling system will detect the status change via notifications
      // Mark that cancel has been requested - button should stay disabled
      setHasRequestedCancel(true)
      
      // Don't stop polling - let it continue to detect status changes
      // The polling system will handle the status change when it arrives
      
    } catch (error) {
      setPaymentError(error instanceof Error ? error.message : 'Failed to cancel payment. Please try again.')
      // On error, allow retry - don't set hasRequestedCancel
      // Continue polling even on error - payment might still be processing
    } finally {
      setIsCancelling(false)
    }
  }

  const handleRetryPayment = () => {
    // Stop any active polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }

    setPaymentState('idle')
    setCurrentOrder(null)
    setCurrentReceipt(null)
    setPaymentError(null)
    setIsCancelling(false)
    setHasRequestedCancel(false)
    setPollingAttempts(0)
    // currentOrderId / currentOrderNumber deliberately stay: the order exists and is unpaid,
    // and the next "Complete Order" pays IT again (retryOrderPayment) rather than creating a
    // second order. Closing the modal is what forgets it.
    setCurrentPaymentId(null)
    setCurrentPayment(null)
    setHasStartedPolling(false)
    setTerminalMessage(null)
    setConsumingEmployeeId(null)
  }

  const handleModalClose = () => {
    // Stop any active polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current)
      pollingIntervalRef.current = null
    }
    
    setPaymentState('idle')
    setCurrentOrder(null)
    setCurrentReceipt(null)
    setPaymentError(null)
    setIsCancelling(false)
    setHasRequestedCancel(false)
    setPollingAttempts(0)
    setCurrentOrderId(null)
    setCurrentPaymentId(null)
    setCurrentPayment(null)
    setHasStartedPolling(false)
    setTerminalMessage(null)
    
    onClose()
  }

  const formatPrice = (cents: number) => {
    return `€${(cents / 100).toFixed(2)}`
  }

  const calculateItemTotal = (item: any) => {
    const basePrice = item.price_cents * item.quantity
    const optionSurcharges = item.selected_options?.reduce(
      (sum: number, opt: any) => sum + (opt.price_delta_cents || 0), 0
    ) || 0
    return basePrice + (optionSurcharges * item.quantity)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleModalClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">Checkout</h2>
            <button
              onClick={handleModalClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Cash Confirmation Dialog */}
          {showCashConfirmation && (
            <div className="fixed inset-0 z-60 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowCashConfirmation(false)} />
                
                <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
                  <div className="p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="flex-shrink-0">
                        <BanknotesIcon className="h-8 w-8 text-yellow-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Cash Payment Confirmation</h3>
                        <p className="text-sm text-gray-600">This will complete the order immediately</p>
                      </div>
                    </div>
                    
                    <div className="bg-yellow-50 rounded-lg p-4 mb-6">
                      <div className="space-y-2">
                        <p className="text-sm text-yellow-800">
                          <strong>Important:</strong> Cash payments complete the order immediately without waiting for payment verification.
                        </p>
                        <p className="text-sm text-yellow-800">
                          Make sure you have received the cash amount of <strong>{formatPrice(payableTotalCents)}</strong> before proceeding.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-end space-x-3">
                      <button
                        onClick={() => handleCashConfirmation(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleCashConfirmation(true)}
                        className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 border border-transparent rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      >
                        Confirm Cash Payment
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="p-6 space-y-6 max-h-[calc(90vh-140px)] overflow-y-auto">
            {/* Error Message Display - Show when in idle state and there's an error */}
            {paymentState === 'idle' && paymentError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <p className="text-sm font-medium text-red-800">{paymentError}</p>
                  </div>
                  <div className="ml-auto pl-3">
                    <button
                      onClick={() => setPaymentError(null)}
                      className="inline-flex text-red-400 hover:text-red-600"
                    >
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Only show form sections when not processing payment */}
            {paymentState === 'idle' && (
              <>
            {/* Paying an existing order again: the card was declined or cancelled, the order
                stands unpaid, and this attempt goes to IT (retryOrderPayment). Said on screen —
                the POS runs on touchscreens, so never in a tooltip. The customer fields are
                hidden: the order already carries its name, type and note, and the retry does
                not send them. */}
            {currentOrderId && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
                <p className="font-medium">
                  Paying order {currentOrderNumber ?? `#${currentOrderId}`} again
                </p>
                <p className="mt-1 text-blue-800">
                  The previous payment did not complete. Choose Cash or Card below — the same order is
                  paid, no new order is created, and the amount is what is still open on it.
                </p>
              </div>
            )}

            {/* Customer Information */}
            {!currentOrderId && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Customer Information</h3>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Name (Optional)
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => handleCustomerNameChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter customer name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order Type
                </label>
                <p className="px-3 py-2 text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-md">
                  {cart.order_type === 'DINE_IN' ? 'Dine In' : 'Takeaway'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order Note (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder='Note for the kitchen, e.g. "Allergy: peanuts — separate prep"'
                />
              </div>
            </div>
            )}

            {/* Order Summary */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Order Summary</h3>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-2">
                  {cart.items.map((item) => (
                    <div key={item.id} className="flex justify-between items-center">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">
                          {item.product_name} - {item.product_variant_name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {item.quantity}x {formatPrice(item.price_cents)}
                        </p>
                        {item.selected_options?.length > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            {item.selected_options.map((opt, idx) => (
                              <div key={idx} className="flex justify-between">
                                <span>
                                  {opt.option_item_label}
                                  {idx < item.selected_options.length - 1 ? ', ' : ''}
                                </span>
                                {opt.price_delta_cents > 0 && (
                                  <span className="text-green-600 font-medium">
                                    +{formatPrice(opt.price_delta_cents)}
                                  </span>
                                )}
                                {opt.price_delta_cents < 0 && (
                                  <span className="text-red-600 font-medium">
                                    {formatPrice(opt.price_delta_cents)}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <p className="font-medium text-gray-900">
                        {formatPrice(calculateItemTotal(item))}
                      </p>
                    </div>
                  ))}
                </div>
                
                <div className="border-t border-gray-200 mt-4 pt-4 space-y-1">
                  {couponCode && (
                    <div className="flex justify-between items-center text-sm text-green-700">
                      <span>Coupon: {couponCode}</span>
                      <span>applied</span>
                    </div>
                  )}
                  {depositTotalCents > 0 && (
                    <div className="flex justify-between items-center text-sm text-gray-600">
                      <span>Statiegeld</span>
                      <span>{formatPrice(depositTotalCents)}</span>
                    </div>
                  )}
                  {packagingFeeCents > 0 && (
                    <div className="flex justify-between items-center text-sm text-gray-600">
                      <span>Verpakkingskosten</span>
                      <span>{formatPrice(packagingFeeCents)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center text-lg font-semibold">
                    <span>Total</span>
                    <span>{formatPrice(payableTotalCents)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Payment Method</h3>

              {/* Staff is never offered on a retry: the API refuses staff_meal on retryOrderPayment. */}
              <div className={`grid gap-4 ${hasPermission(PERMISSIONS.STAFF_MEAL_CREATE) && !currentOrderId ? 'grid-cols-3' : 'grid-cols-2'}`}>
                <button
                  onClick={() => handlePaymentMethodChange('CASH')}
                  className={`p-4 border-2 rounded-lg flex items-center justify-center space-x-2 ${
                    paymentMethod === 'CASH'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <BanknotesIcon className="h-6 w-6" />
                  <span className="font-medium">Cash</span>
                </button>

                <button
                  onClick={() => handlePaymentMethodChange('CARD')}
                  className={`p-4 border-2 rounded-lg flex items-center justify-center space-x-2 ${
                    paymentMethod === 'CARD'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <CreditCardIcon className="h-6 w-6" />
                  <span className="font-medium">Card</span>
                </button>

                {hasPermission(PERMISSIONS.STAFF_MEAL_CREATE) && !currentOrderId && (
                  <button
                    onClick={() => handlePaymentMethodChange('STAFF_MEAL')}
                    className={`p-4 border-2 rounded-lg flex items-center justify-center space-x-2 ${
                      paymentMethod === 'STAFF_MEAL'
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <BanknotesIcon className="h-6 w-6 text-orange-600" />
                    <span className="font-medium">Staff</span>
                  </button>
                )}
              </div>
            </div>

            {/* Staff Meal — employee picker, shown when Staff is selected */}
            {paymentMethod === 'STAFF_MEAL' && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-3">
                <p className="text-sm text-orange-800">
                  Staff meal — 100% discount is applied automatically. The order flows to the kitchen as normal.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Consuming Employee <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={consumingEmployeeId ?? ''}
                    onChange={(e) => setConsumingEmployeeId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white"
                  >
                    <option value="">Select employee...</option>
                    {activeUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
              </>
            )}
          </div>

          {/* Payment Processing State */}
          {paymentState === 'processing' && (
            <div className="p-6 border-t border-gray-200 bg-blue-50">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <div>
                    <p className="text-lg font-semibold text-blue-900">
                      Processing payment...
                    </p>
                    <p className="text-sm text-blue-700">
                      {paymentMethod === 'CARD' 
                        ? 'Please wait while we verify your card payment'
                        : 'Please wait while we process your cash payment'
                      }
                    </p>
                    {/* TODO: Show polling status for card payments */}
                    {paymentMethod === 'CARD' && pollingAttempts > 0 && (
                      <p className="text-xs text-yellow-600 mt-1">
                        Checking payment status... ({pollingAttempts}/250)
                      </p>
                    )}
                  </div>
                </div>

                {/* Terminal Message (from polling) */}
                {paymentMethod === 'CARD' && terminalMessage && (
                  <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="animate-pulse">
                        <CreditCardIcon className="h-5 w-5 text-yellow-600" />
                      </div>
                      <p className="text-sm font-medium text-yellow-900 whitespace-pre-line">
                        {terminalMessage}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Processing Details */}
                <div className="bg-white rounded-lg p-4 mb-4 text-left">
                  <div className="space-y-3">
                    <div>
                      <p className="font-medium text-gray-900">Payment Method</p>
                      <p className="text-sm text-gray-600 capitalize">{paymentMethod}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Amount</p>
                      <p className="text-sm text-gray-600 font-semibold">{formatPrice(payableTotalCents)}</p>
                    </div>
                    {paymentMethod === 'CARD' && (
                      <div>
                        <p className="font-medium text-gray-900">Processing Time</p>
                        <p className="text-sm text-gray-600">This may take 1-3 seconds</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Cancel Button - Only show for card payments that can be cancelled */}
                {(() => {
                  const payment = currentPayment || currentOrder?.payment
                  if (!payment) return null
                  
                  const paymentStatus = (payment.status || '').toUpperCase()
                  const canCancel = (paymentStatus === 'PENDING' || paymentStatus === 'UNKNOWN')
                    && payment.payment_method === 'CARD'
                  
                  if (!canCancel) return null
                  
                  const isDisabled = isCancelling || hasRequestedCancel
                  
                  return (
                    <button
                      onClick={handleCancelPayment}
                      disabled={isDisabled}
                      className="w-full px-6 py-3 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isCancelling ? (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                          <span>Cancelling Payment...</span>
                        </div>
                      ) : hasRequestedCancel ? (
                        'Cancel Request Sent'
                      ) : (
                        'Cancel Payment'
                      )}
                    </button>
                  )
                })()}
              </div>
            </div>
          )}

          {/* Payment Success State */}
          {paymentState === 'completed' && currentOrder && (() => {
            const paymentStatus = (currentPayment?.status || currentOrder.payment?.status || '').toUpperCase()
            const isUnknown = paymentStatus === 'UNKNOWN'
            
            return (
              <div className="p-6 border-t border-gray-200 bg-green-50">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <div>
                    <p className="text-lg font-semibold text-green-900">Order Confirmed!</p>
                    <p className="text-sm text-green-700">Your order has been processed</p>
                  </div>
                </div>
                
                {/* Order Details */}
                <div className="bg-white rounded-lg p-4 mb-4 text-left">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-medium text-gray-900">Order Number</p>
                      <p className="text-gray-600">{currentOrder.order_number}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Total Amount</p>
                      <p className="text-gray-600 font-semibold">{formatPrice(currentOrder.total_cents)}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Payment Method</p>
                      <p className="text-gray-600 capitalize">{currentOrder.payment?.payment_method || paymentMethod}</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Payment Status</p>
                      <p className={`font-semibold capitalize ${
                        isUnknown ? 'text-blue-600' : 'text-green-600'
                      }`}>
                        {isUnknown ? 'Verifying' : (currentPayment?.status || currentOrder.payment?.status || 'Completed').toLowerCase()}
                      </p>
                    </div>
                  </div>
                  
                  {/* UNKNOWN Payment Status Notice */}
                  {isUnknown && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-blue-900 mb-1">Payment Status: Verifying</p>
                        <p className="text-xs text-blue-800">
                          The order has been confirmed and can be processed normally. The payment status will be updated automatically when determined.
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Transaction ID if available */}
                  {currentOrder.payment?.transaction_id && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500">Transaction ID: {currentOrder.payment.transaction_id}</p>
                    </div>
                  )}
                </div>
                
                {/* Payment Action Prompts - Show when payment is COMPLETED and flags are true */}
                {paymentStatus === 'COMPLETED' && (() => {
                  // Use currentPayment if available, otherwise fall back to currentOrder.payment
                  const payment = currentPayment || currentOrder?.payment
                  if (!payment) return null
                  
                  const hasActions = 
                    payment.ask_merchant_signature === true ||
                    payment.ask_customer_signature === true ||
                    payment.ask_customer_identification === true ||
                    payment.print_customer_receipt === true
                  
                  if (!hasActions) return null
                  
                  return (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                      <h3 className="text-sm font-semibold text-yellow-900 mb-3">Required Actions</h3>
                      <div className="space-y-3">
                        {/* Merchant Signature Required */}
                        {payment.ask_merchant_signature === true && (
                          <div className={`bg-white rounded-lg p-3 border-2 ${
                            completedActions.has('merchant_signature') 
                              ? 'border-green-300 bg-green-50' 
                              : 'border-yellow-300'
                          }`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900 mb-1">
                                  ⚠️ Merchant Signature Required
                                </p>
                                <p className="text-xs text-gray-600">
                                  Please sign the receipt and store it.
                                </p>
                                {completedActions.has('merchant_signature') && (
                                  <p className="text-xs text-green-700 mt-1 font-medium">✅ Completed</p>
                                )}
                              </div>
                              {!completedActions.has('merchant_signature') && (
                                <button
                                  onClick={handleMerchantSignatureComplete}
                                  className="ml-3 px-3 py-1 text-xs font-medium text-white bg-yellow-600 rounded hover:bg-yellow-700"
                                >
                                  Mark Complete
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Customer Signature Required */}
                        {payment.ask_customer_signature === true && (
                          <div className={`bg-white rounded-lg p-3 border-2 ${
                            completedActions.has('customer_signature') 
                              ? 'border-green-300 bg-green-50' 
                              : 'border-yellow-300'
                          }`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900 mb-1">
                                  ⚠️ Customer Signature Required
                                </p>
                                <p className="text-xs text-gray-600">
                                  Please have the customer sign the receipt and store it.
                                </p>
                                {completedActions.has('customer_signature') && (
                                  <p className="text-xs text-green-700 mt-1 font-medium">✅ Completed</p>
                                )}
                              </div>
                              {!completedActions.has('customer_signature') && (
                                <button
                                  onClick={handleCustomerSignatureComplete}
                                  className="ml-3 px-3 py-1 text-xs font-medium text-white bg-yellow-600 rounded hover:bg-yellow-700"
                                >
                                  Mark Complete
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Customer Identification Required */}
                        {payment.ask_customer_identification === true && (
                          <div className={`bg-white rounded-lg p-3 border-2 ${
                            completedActions.has('customer_identification') 
                              ? 'border-green-300 bg-green-50' 
                              : 'border-yellow-300'
                          }`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900 mb-1">
                                  ⚠️ Customer Identification Required
                                </p>
                                <p className="text-xs text-gray-600">
                                  Please check the customer's ID and write the passport number or driver's license number on the receipt by hand and store it.
                                </p>
                                {completedActions.has('customer_identification') && (
                                  <p className="text-xs text-green-700 mt-1 font-medium">✅ Completed</p>
                                )}
                              </div>
                              {!completedActions.has('customer_identification') && (
                                <button
                                  onClick={handleCustomerIDComplete}
                                  className="ml-3 px-3 py-1 text-xs font-medium text-white bg-yellow-600 rounded hover:bg-yellow-700"
                                >
                                  Mark Complete
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Print Customer Receipt Required */}
                        {payment.print_customer_receipt === true && (
                          <div className={`bg-white rounded-lg p-3 border-2 ${
                            completedActions.has('print_customer_receipt') 
                              ? 'border-green-300 bg-green-50' 
                              : 'border-yellow-300'
                          }`}>
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900 mb-1">
                                  ⚠️ Print Customer Receipt Required
                                </p>
                                <p className="text-xs text-gray-600">
                                  Printing a receipt for the customer is mandatory.
                                </p>
                                {completedActions.has('print_customer_receipt') && (
                                  <p className="text-xs text-green-700 mt-1 font-medium">✅ Receipt Printed</p>
                                )}
                                {!currentReceipt && !completedActions.has('print_customer_receipt') && (
                                  <p className="text-xs text-yellow-700 mt-1">Loading receipt...</p>
                                )}
                              </div>
                              {!completedActions.has('print_customer_receipt') && currentReceipt && (
                                <button
                                  onClick={handleReceiptPrintComplete}
                                  className="ml-3 px-3 py-1 text-xs font-medium text-white bg-yellow-600 rounded hover:bg-yellow-700"
                                >
                                  Print Receipt
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                })()}
                
                {/* Action Buttons */}
                <div className="space-y-3">
                  {/* Receipt Actions */}
                  {(() => {
                    const payment = currentPayment || currentOrder?.payment
                    if (!payment) return null
                    
                    const merchantReceiptNeeded =
                      payment.ask_customer_signature === true ||
                      payment.ask_customer_identification === true
                    
                    return (
                      <>
                        {/* Merchant Receipt Actions - Only show if merchant receipt is needed */}
                        {merchantReceiptNeeded && (
                          <div className="space-y-2">
                            <p className="text-xs font-medium text-gray-700">Merchant receipt actions</p>
                            {merchantReceipt ? (
                              <div className="grid grid-cols-3 gap-2">
                                <button
                                  onClick={() => ReceiptUtils.printMerchantReceipt(merchantReceipt)}
                                  className="flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <PrinterIcon className="h-4 w-4" />
                                  <span>Print</span>
                                </button>
                                
                                <button
                                  onClick={() => ReceiptUtils.viewMerchantReceipt(merchantReceipt)}
                                  className="flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                  <span>View</span>
                                </button>
                                
                                <button
                                  onClick={async () => {
                                    const url = merchantReceipt.receipt_url
                                    if (url) {
                                      const shareUrl = new URL(url, window.location.origin)
                                      shareUrl.pathname = `/receipt/${merchantReceipt.receipt_id}`
                                      shareUrl.searchParams.set('type', 'MERCHANT')
                                      await navigator.clipboard.writeText(shareUrl.toString())
                                      alert('Merchant receipt URL copied to clipboard!')
                                    }
                                  }}
                                  className="flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                  <ShareIcon className="h-4 w-4" />
                                  <span>Share</span>
                                </button>
                              </div>
                            ) : isLoadingMerchantReceipt ? (
                              <div className="text-xs text-gray-500 py-2">Loading merchant receipt...</div>
                            ) : (
                              <div className="text-xs text-gray-500 py-2">Merchant receipt not available</div>
                            )}
                          </div>
                        )}
                        
                        {/* Customer Receipt Actions - Always show */}
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-gray-700">Customer receipt actions</p>
                          {currentReceipt ? (
                            <div className="grid grid-cols-3 gap-2">
                              <button
                                onClick={() => ReceiptUtils.printReceipt(currentReceipt)}
                                className="flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <PrinterIcon className="h-4 w-4" />
                                <span>Print</span>
                              </button>
                              
                              <button
                                onClick={() => ReceiptUtils.viewReceipt(currentReceipt)}
                                className="flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <EyeIcon className="h-4 w-4" />
                                <span>View</span>
                              </button>
                              
                              <button
                                onClick={async () => {
                                  const success = await ReceiptUtils.shareReceipt(currentReceipt)
                                  if (success) {
                                    alert('Receipt URL copied to clipboard!')
                                  }
                                }}
                                className="flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <ShareIcon className="h-4 w-4" />
                                <span>Share</span>
                              </button>
                            </div>
                          ) : isLoadingCustomerReceipt ? (
                            <div className="text-xs text-gray-500 py-2">Loading customer receipt...</div>
                          ) : (
                            <div className="text-xs text-gray-500 py-2">Customer receipt not available</div>
                          )}
                        </div>
                      </>
                    )
                  })()}
                  
                  {/* Complete & Close Button */}
                  <button
                    onClick={() => {
                      if (currentOrder) {
                        onOrderCreated(currentOrder)
                      }
                      handleModalClose()
                    }}
                    className="w-full px-6 py-3 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    Complete & Close
                  </button>
                </div>
              </div>
            </div>
            )
          })()}


          {/* Payment Pending State (Timeout) */}
          {paymentState === 'pending' && (
            <div className="p-6 border-t border-gray-200 bg-yellow-50">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <svg className="h-8 w-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <p className="text-lg font-semibold text-yellow-900">Payment Timeout</p>
                    <p className="text-sm text-yellow-700">Payment verification timed out</p>
                  </div>
                </div>
                
                {/* Timeout Details */}
                <div className="bg-white rounded-lg p-4 mb-4 text-left">
                  <div className="space-y-3">
                    <div>
                      <p className="font-medium text-gray-900">What happened?</p>
                      <p className="text-sm text-gray-600">
                        Payment verification timed out after 30 seconds. The payment may still be processing on the terminal.
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Next steps</p>
                      <p className="text-sm text-gray-600">Please check with staff to verify the payment status manually.</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Your cart</p>
                      <p className="text-sm text-gray-600">Items are preserved until payment is confirmed</p>
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={handleRetryPayment}
                    className="w-full px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Check Payment Status
                  </button>
                  <button
                    onClick={handleModalClose}
                    className="w-full px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Close & Keep Cart
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Payment Failed State */}
          {paymentState === 'failed' && (
            <div className="p-6 border-t border-gray-200 bg-red-50">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <div>
                    <p className="text-lg font-semibold text-red-900">Payment Failed</p>
                    <p className="text-sm text-red-700">We couldn't process your payment</p>
                  </div>
                </div>
                
                {/* Error Details */}
                <div className="bg-white rounded-lg p-4 mb-4 text-left">
                  <div className="space-y-3">
                    <div>
                      <p className="font-medium text-gray-900">What happened?</p>
                      <p className="text-sm text-gray-600">
                        {paymentError || (
                          paymentMethod === 'CARD' 
                            ? 'Your card payment could not be processed. This might be due to insufficient funds, card declined, or network issues.'
                            : 'Your cash payment could not be processed. Please check the amount and try again.'
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Your cart</p>
                      <p className="text-sm text-gray-600">All items have been preserved and are ready for checkout</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Total amount</p>
                      <p className="text-sm text-gray-600 font-semibold">{formatPrice(payableTotalCents)}</p>
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={handleRetryPayment}
                    className="w-full px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Try Payment Again
                  </button>
                  <button
                    onClick={handleModalClose}
                    className="w-full px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Close & Keep Cart
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Payment Cancelled State */}
          {paymentState === 'cancelled' && (
            <div className="p-6 border-t border-gray-200 bg-yellow-50">
              <div className="text-center">
                <div className="flex items-center justify-center space-x-3 mb-4">
                  <svg className="h-8 w-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <div>
                    <p className="text-lg font-semibold text-yellow-900">Payment Cancelled</p>
                    <p className="text-sm text-yellow-700">You cancelled the payment process</p>
                  </div>
                </div>
                
                {/* Cancellation Details */}
                <div className="bg-white rounded-lg p-4 mb-4 text-left">
                  <div className="space-y-3">
                    <div>
                      <p className="font-medium text-gray-900">What happened?</p>
                      <p className="text-sm text-gray-600">
                        {paymentError || 'You chose to cancel the payment while it was being processed. No charges have been made.'}
                      </p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Your cart</p>
                      <p className="text-sm text-gray-600">All items are still in your cart and ready for checkout</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Total amount</p>
                      <p className="text-sm text-gray-600 font-semibold">{formatPrice(payableTotalCents)}</p>
                    </div>
                  </div>
                </div>
                
                {/* Action Buttons */}
                <div className="space-y-3">
                  <button
                    onClick={handleRetryPayment}
                    className="w-full px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    Try Payment Again
                  </button>
                  <button
                    onClick={handleModalClose}
                    className="w-full px-6 py-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Close & Keep Cart
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Default Footer - Only show when not processing */}
          {paymentState === 'idle' && (
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
                  onClick={handleModalClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              onClick={handleCheckout}
              disabled={isProcessing || cart.items.length === 0 || (paymentMethod === 'STAFF_MEAL' && !consumingEmployeeId)}
              className={`px-6 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                paymentMethod === 'STAFF_MEAL'
                  ? 'bg-orange-600 hover:bg-orange-700 focus:ring-orange-500'
                  : 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
              }`}
            >
              {paymentMethod === 'STAFF_MEAL' ? 'Submit Staff Meal' : currentOrderId ? 'Pay Order Again' : 'Complete Order'}
            </button>
          </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CheckoutModal
